// DataBreachInfo.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import UserSidebar from "./UserSidebar";
import UserHeader from "./UserHeader";
import { BASE_URL } from "../api_call";
import { IoIosInformationCircle } from "react-icons/io";
import "./Dashboard.css";

export default function DataBreachInfo() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [reports, setReports] = useState([]);
  const [successMsg, setSuccessMsg] = useState("");
  const [successVisible, setSuccessVisible] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [loading, setLoading] = useState(true);
  const [files, setFiles] = useState({});
  const navigate = useNavigate();

  // ---------------- Fetch reports on load ----------------
  useEffect(() => {
    const taskNumber = localStorage.getItem("taskNumber");
    if (!taskNumber) {
      setErrorMsg("Report number not found.");
      setLoading(false);
      return;
    }

    const fetchReports = async () => {
      try {
        setLoading(true);
        setErrorMsg("");
        const token = localStorage.getItem("access_token");

        const res = await fetch(`${BASE_URL}/data-breach/view/${taskNumber}`, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        });

        const data = await res.json();
        if (res.status !== 200) throw data;

        // API now returns array: data.tasks
        setReports(data.tasks || []);
      } catch (err) {
        console.error(err);
        setErrorMsg(err.error || "❌ Failed to fetch reports");
        setReports([]);
      } finally {
        setLoading(false);
      }
    };

    fetchReports();
  }, []);
// ---------------- Delete All Reports ----------------
const handleDeleteAll = async () => {
  const taskNumber = localStorage.getItem("taskNumber");
  if (!taskNumber) {
    alert("Report number not found");
    return;
  }

  const confirmDelete = window.confirm(
    "Are you sure you want to delete all reports for this task?"
  );
  if (!confirmDelete) return;

  try {
    const token = localStorage.getItem("access_token");
    const res = await fetch(`${BASE_URL}/data-breach/delete`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ task_number: taskNumber }),
    });

    const data = await res.json();
    if (res.status !== 200) throw data;

    setSuccessMsg(data.message || "✅ All reports deleted successfully");
    setErrorMsg("");
    setReports([]); // clear table instantly
  } catch (err) {
    console.error(err);
    setErrorMsg(err.error || "❌ Failed to delete reports");
    setSuccessMsg("");
  }
};
  // ---------------- File upload for sending to approver ----------------
  const handleFileChange = (index, file) =>
    setFiles((prev) => ({ ...prev, [index]: file }));

  // ---------------- Send to Approver ----------------
  const handleSendToApprover = async (report, index) => {
  if (!report.approver_email) {
    alert("Enter approver email");
    return;
  }

  if (!report.task_id) {
    alert("Task ID missing");
    return;
  }

  const formData = new FormData();
  formData.append("approver_email", report.approver_email);
  formData.append("task_id", report.task_id);
  if (files[index]) formData.append("attachment", files[index]);

  try {
    const token = localStorage.getItem("access_token");

    const res = await fetch(`${BASE_URL}/data-breach/send-to-approver`, {
      method: "POST",
      body: formData,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const data = await res.json();
    if (res.status !== 200) throw data;

    setSuccessMsg("✅ Sent to approver successfully");
    setErrorMsg("");

    // Optional: update status locally for instant UI feedback
    setReports((prev) => {
      const updated = [...prev];
      updated[index].task_status = "Requested";
      return updated;
    });

    setFiles((prev) => ({ ...prev, [index]: null }));

  } catch (err) {
    console.error(err);
    setErrorMsg(err.error || "❌ Failed to send to approver");
    setSuccessMsg("");
  }
};

  // ---------------- Auto-hide Success ----------------
  useEffect(() => {
    if (successMsg) {
      setSuccessVisible(true);
      const timer = setTimeout(() => setSuccessVisible(false), 4500);
      const removeTimer = setTimeout(() => setSuccessMsg(""), 5000);
      return () => {
        clearTimeout(timer);
        clearTimeout(removeTimer);
      };
    }
  }, [successMsg]);

  const handleEdit = (report) => {
  if (report.task_status?.toLowerCase() === "approved") {
    alert("Approved reports cannot be edited");
    return;
  }

  localStorage.setItem("taskId", report.task_id);
  localStorage.setItem("taskActionDate", report.task_action_date || "");
  localStorage.setItem("taskReminderDays", report.task_reminder_days || 1);
  localStorage.setItem("taskEscalationEmail", report.task_escalation_email || "");
  localStorage.setItem("taskEscalationReminderDays", report.task_escalation_reminder_days || 0);

  navigate("/data-breach");
};
const statusCounts = React.useMemo(() => {
  const counts = {
    total: 0,
    pending: 0,
    requested: 0,
    approved: 0,
    declined: 0,   // ✅ added
  };

  reports.forEach((item) => {
    counts.total++;

    const status = item.task_status?.toLowerCase();

    if (status === "pending") counts.pending++;
    else if (status === "requested") counts.requested++;
    else if (status === "approved") counts.approved++;
    else if (status === "declined") counts.declined++; // ✅ added
  });

  return counts;
}, [reports]);

  return (
    <div className="edit-compliance">
      <UserSidebar menuOpen={menuOpen} setMenuOpen={setMenuOpen} />
      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <UserHeader menuOpen={menuOpen} setMenuOpen={setMenuOpen} />

        <main style={{ flex: 1, padding: "1rem 2rem", overflowY: "auto" }}>
          <header>
            <h2>Data Breach Reports</h2>
          </header>

          <div className="messages-top">
            {errorMsg && <div className="login-error">{errorMsg}</div>}
            {successMsg && (
              <div className={`success-msg ${!successVisible ? "fade-out" : ""}`}>
                {successMsg}
              </div>
            )}
          </div>

          <div
  style={{
    display: "flex",
    justifyContent: "flex-end",
    marginBottom: "10px",
  }}
>
  <div style={{ display: "flex", gap: "10px" }}>

    <div style={{
      background: "#e2e3e5",
      color: "#383d41",
      padding: "6px 12px",
      borderRadius: "20px",
      fontWeight: "600",
      fontSize: "13px"
    }}>
      Total: {statusCounts.total}
    </div>

    <div style={{
      background: "#f8d7da",
      color: "#721c24",
      padding: "6px 12px",
      borderRadius: "20px",
      fontWeight: "600",
      fontSize: "13px"
    }}>
      Pending: {statusCounts.pending}
    </div>

    <div style={{
      background: "#fff3cd",
      color: "#856404",
      padding: "6px 12px",
      borderRadius: "20px",
      fontWeight: "600",
      fontSize: "13px"
    }}>
      Requested: {statusCounts.requested}
    </div>

    <div style={{
      background: "#d4edda",
      color: "#155724",
      padding: "6px 12px",
      borderRadius: "20px",
      fontWeight: "600",
      fontSize: "13px"
    }}>
      Approved: {statusCounts.approved}
    </div>
    <div style={{
  background: "#ffb4b4",   // light orange
  color: "#590a0a",        // dark orange text
  padding: "6px 12px",
  borderRadius: "20px",
  fontWeight: "600",
  fontSize: "13px"
}}>
  Declined: {statusCounts.declined}
</div>

  </div>
</div>

          {/* ---------------- Table ---------------- */}
          <div className="table-wrapper">
            <table className="data-table" style={{ tableLayout: "fixed", width: "100%" }}>
              <thead>
                <tr>
                  <th style={{ width: "180px" }}>ACTION</th>
                  <th style={{ width: "400px" }}>DESCRIPTION</th>
                  <th style={{ width: "100px" }}>START DATE</th>
                  <th style={{ width: "100px" }}>ACTION DATE</th>
                  <th style={{ width: "100px" }}>END DATE</th>
                  <th style={{ width: "90px" }}>REM. BEFORE</th>
                  <th style={{ width: "100px" }}>STATUS</th>
                  <th style={{ width: "120px" }}>APPROVER’S EMAIL</th>
                  <th style={{ width: "140px" }}>ATTACHMENT</th>
                  <th style={{ width: "120px" }}>ACTION</th>
                  <th style={{ width: "60px" }}>EDIT</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="11" style={{ textAlign: "center" }}>Loading...</td>
                  </tr>
                ) : reports.length === 0 ? (
                  <tr>
                    <td colSpan="11" style={{ textAlign: "center" }}>No reports found</td>
                  </tr>
                ) : (
                  reports.map((report, index) => (
                    <tr key={index}>
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                          <span>{report.task_title || report.task_particular}</span>
                          {report.task_long_description && (
                            <IoIosInformationCircle
                              title={report.task_description}
                              style={{ cursor: "pointer", fontSize: "16px", color: "#1A1744" }}
                            />
                          )}
                        </div>
                      </td>

                      <td style={{ maxWidth: "400px" }}>
                        <div className="description-scroll">{report.task_long_description}</div>
                      </td>
                      <td>{report.task_start_date}</td>
                      <td>{report.task_action_date}</td>
                      <td>{report.task_end_date}</td>
                      <td>{report.task_reminder_days ? `${report.task_reminder_days} Days` : "-"}</td>
                      <td style={{
                        color:
                          report.task_status?.toLowerCase() === "approved"
                            ? "green"
                            : report.task_status?.toLowerCase() === "requested"
                            ? "orange"
                            : "red",
                        fontWeight: 500,
                      }}>
                        {report.task_status}
                      </td>

                      <td>
                        <input
                          type="email"
                          className="approver-input"
                          value={report.approver_email || ""}
                          onChange={(e) =>
                            setReports((prev) => {
                              const updated = [...prev];
                              updated[index].approver_email = e.target.value;
                              return updated;
                            })
                          }
                          placeholder="Enter email"
                        />
                      </td>

                      <td>
                        <label className="file-upload-label">
                          <input
                            type="file"
                            onChange={(e) => handleFileChange(index, e.target.files[0])}
                            hidden
                          />
                          <span className="custom-file-btn">
                            {files[index] ? files[index].name : "Attach evidence"}
                          </span>
                        </label>
                      </td>

                      <td>
                        <button className="action-btn primary" onClick={() => handleSendToApprover(report, index)} title="Send to approver">
                          ✔
                        </button>
                      </td>

                      <td>
  <button
    className="action-btn edit"
    onClick={() => handleEdit(report)}
    disabled={report.task_status?.toLowerCase() === "approved"}
    style={{
      opacity: report.task_status?.toLowerCase() === "approved" ? 0.5 : 1,
      cursor: report.task_status?.toLowerCase() === "approved" ? "not-allowed" : "pointer"
    }}
  >
    ✎
  </button>
</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            <div style={{ textAlign: "center", marginTop: "25px" }}>
  <button
    className="submit-btn"
    style={{ width: "95%" }}
    onClick={handleDeleteAll}
  >
    Delete All
  </button>
</div>
          </div>
        </main>
      </div>
    </div>
  );
}